/**
 * [INV-SPLIT 2026-10-07, user] "Retention by PM" — opens from Edit Mapping on a payment whose website
 * is split between PMs.
 *
 * [SPLIT-RET-BY-PM 2026-10-08, user] Reworked: it opens AT ONCE when a Retention box is clicked (and
 * still from Save when the retention and its PM shares disagree), lists EVERY PM on the payment, and
 * takes an amount against each name. Two checks, live as you type — the user's rules:
 *   • a PM's amount can be at most their open part (their part of the website this month, less what
 *     the website's OTHER payments already gave them);
 *   • together the amounts can never be more than the payment amount (less whatever this payment
 *     already maps in other categories).
 * Map is off while either rule is broken; the message says which and by how much.
 *
 * Look: the user-approved popup (artifact WywpMW9AW1g1GJ2ogiszgi) — shade + 3px blur behind it
 * (4px+ makes the window's text glow — rejected the same day), glass box, green accents.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
// [LIQUID-GLASS 2026-11-02, user] the box, rows, amount boxes and buttons use the shared liquid glass recipe
import {
  lgSurface, lgSquircle, lgHair, lgGreen, lgCardSheen, lgButtonPrimary, lgButtonSecondary, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass';
import { fluid } from '../styles/motion'; // [FLUID-3 2026-10-12]
import { usePresence } from '../hooks/usePresence'; // [FLUID-3 2026-10-12]
import { fluidDialog, fluidScrim, fluidCascade } from './Fluid'; // [FLUID-3 2026-10-12]

export interface PmStepPart {
  pm: string;
  /** The PM's part of the website this month. */
  part: number;
  /** Retention already held by the PM on the website's OTHER payments this month. */
  others: number;
  /** The most this payment can give the PM: part − others. */
  open: number;
}

export interface PmStepShare {
  pm: string;
  amount: number;
}

interface Props {
  website: string;
  /** The payment amount (after refunds) — the ceiling the amounts together must respect. */
  paymentAmount: number;
  /** What is still free for retention on this payment: the payment amount less its other categories. */
  maxTotal: number;
  parts: PmStepPart[];
  /** This payment's current shares, to start from (saved, or set earlier in this window). */
  initial: PmStepShare[];
  /** [FLUID-3 2026-10-12] True while Edit Mapping holds the popup mounted for its close (usePresence). */
  closing?: boolean;
  onCancel: () => void;
  onConfirm: (shares: PmStepShare[]) => void;
}

const money = (n: number) => `$${(Math.round(n * 100) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const round = (n: number) => Math.round(n * 100) / 100;
const EPS = 0.005;

const Overlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  /* [INV-SPLIT 2026-10-08, user] A dark shade and only a LITTLE blur. A strong blur (10px, and Edit
     Mapping's own 14px, both tried the same day) turns Edit Mapping's text behind the popup into a
     glowing, half-readable smear — "remove the blur or shiny thing which is showing on the texts" — and
     no blur at all left the eye on the window behind — "a little blur so that all focus will get to the
     main popup". 3px takes the text out of focus; the glow starts at about 4px (compared side by side). */
  background: ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(10, 10, 10, 0.62)' : 'rgba(22, 25, 23, 0.42)'};
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  /* [FLUID-3 2026-10-12, user] The same shade and 3px blur, now fading in and out (it used to appear and
     vanish in one frame) — components/Fluid.tsx. */
  ${(p) => fluidScrim(p.$open)}
`;

/* [FLUID-3 2026-10-12, user] Rises 12px and grows from 0.95 on the spring, closes in 0.18 s; the title, the PM
   rows and the buttons rise in one after another. It had no motion at all before. */
/* [LIQUID-GLASS 2026-11-02, user] The frosted glass of Edit Mapping (light top edge, smooth 24 px corners). The box no
   longer scrolls: Body does (flex column), so the rim never scrolls away. The shade behind keeps its 3px blur. */
const Box = styled.div<{ $open: boolean }>`
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 20, 3) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
  position: relative;
  ${lgSurface(38)}
  ${lgSquircle(24)}
  width: 560px;
  max-width: 100%;
  max-height: calc(100vh / var(--app-zoom, 1) - 32px); /* [APP-FIT 2026-10-26] the window in page px */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: ${(p) => p.theme.text};
  & > * { position: relative; z-index: 1; }
`;

const Head = styled.div`
  padding: 20px 22px 10px;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  h3 { margin: 0; font-size: 17px; font-weight: 700; }
  span { font-size: 13px; color: ${(p) => p.theme.muted}; opacity: 0.8; }
`;

const Body = styled.div`
  padding: 4px 22px 18px;
  display: grid;
  gap: 12px;
  /* [LIQUID-GLASS 2026-11-02] the part that scrolls when the box is taller than the screen */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
`;

const Rows = styled.div`
  display: grid;
  gap: 8px;
`;

/* One PM: the name and their part on the left, the amount box on the right. A row whose amount breaks a
   rule gets the red edge, so the eye lands on the box that needs changing. */
const Row = styled.label<{ $bad: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 150px;
  gap: 12px;
  align-items: center;
  padding: 10px 14px;
  border-radius: 16px; /* [LIQUID-GLASS 2026-11-02] a glass card row: see-through, light top edge */
  cursor: text;
  border: 1px solid ${(p) => (p.$bad
    ? (p.theme.colorScheme === 'dark' ? '#f87171' : '#c81e1e')
    : lgHair(p))};
  background-color: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.58)')};
  background-image: ${lgCardSheen};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.9)')};
  .name { font-weight: 700; font-size: 14px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .left { font-size: 12px; color: ${(p) => p.theme.muted}; opacity: 0.85; font-variant-numeric: tabular-nums; }
`;

/* The same look as Edit Mapping's own money boxes (right-aligned, "$" glued to the value). */
const Amount = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 38px;
  padding: 0 12px;
  border-radius: 12px;
  font: inherit;
  font-size: 14px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: ${(p) => p.theme.text};
  /* [LIQUID-GLASS 2026-11-02] pressed into the glass; a soft green ring and glow while typing (as in Edit Mapping) */
  border: 1px solid transparent;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
    : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)')};
  &:focus {
    outline: none;
    border-color: color-mix(in srgb, ${lgGreen} 70%, transparent);
    box-shadow: 0 0 0 4px color-mix(in srgb, ${lgGreen} 16%, transparent), 0 0 18px color-mix(in srgb, ${lgGreen} 24%, transparent);
  }
`;

const Total = styled.div<{ $bad: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) => (p.$bad ? (p.theme.colorScheme === 'dark' ? '#f87171' : '#c81e1e') : p.theme.text)};
  span:last-child { font-weight: 500; color: ${(p) => p.theme.muted}; }
`;

const Note = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${(p) => p.theme.muted};
  b { color: ${(p) => p.theme.text}; }
`;

const Err = styled.div`
  border-left: 3px solid ${(p) => (p.theme.colorScheme === 'dark' ? '#f87171' : '#c81e1e')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(248,113,113,0.12)' : 'rgba(200,30,30,0.08)')};
  padding: 10px 12px;
  border-radius: 0 10px 10px 0;
  font-size: 13px;
  line-height: 1.5;
  b { color: ${(p) => (p.theme.colorScheme === 'dark' ? '#f87171' : '#c81e1e')}; }
`;

const Foot = styled.div`
  padding: 12px 22px 18px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0; /* [LIQUID-GLASS 2026-11-02] stays put; Body scrolls */
  border-top: 1px solid ${lgHair};
`;

// [LIQUID-GLASS 2026-11-02] Glass capsules (squish + press light).
const Primary = styled.button.attrs<LgPressProps>(LG_PRESS)`${lgButtonPrimary} min-width: 110px;`;
const Secondary = styled.button.attrs<LgPressProps>(LG_PRESS)`${lgButtonSecondary} min-width: 92px;`;

/** Keep only digits and one decimal point — the same cleaning as Edit Mapping's money boxes. */
const clean = (raw: string) => {
  let s = raw.replace(/[^0-9.]/g, '');
  const dot = s.indexOf('.');
  if (dot !== -1) s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
  return s;
};

const RetentionPmPopup: React.FC<Props> = ({ website, paymentAmount, maxTotal, parts, initial, closing = false, onCancel, onConfirm }) => {
  // [FLUID-3 2026-10-12] Open one frame after mount (so the spring has a closed state to start from) until Edit
  // Mapping starts the close.
  const open = usePresence(!closing, fluid.dialogOutMs).entered;
  // One text value per PM, started from the payment's current shares (blank = nothing for that PM).
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {};
    for (const p of parts) {
      const cur = initial.filter((s) => s.pm === p.pm).reduce((t, s) => t + (Number(s.amount) || 0), 0);
      v[p.pm] = cur > EPS ? String(round(cur)) : '';
    }
    return v;
  });
  const firstRef = useRef<HTMLInputElement>(null);

  // [FLUID-3 2026-10-12] Escape only while open: a closing popup is already on its way out (it used to unmount
  // at once, taking this listener with it).
  useEffect(() => {
    if (closing) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, closing]);
  // The cursor lands in the first PM's box, so typing can start at once.
  useEffect(() => { firstRef.current?.focus(); }, []);

  const amounts = useMemo(() => parts.map((p) => ({ ...p, amount: round(parseFloat(values[p.pm]) || 0) })), [parts, values]);
  const total = round(amounts.reduce((t, a) => t + a.amount, 0));
  const overPart = amounts.filter((a) => a.amount > a.open + EPS);
  const ceiling = round(Math.max(0, Math.min(paymentAmount, maxTotal)));
  const overTotal = total > ceiling + EPS;
  const otherCats = round(Math.max(0, paymentAmount - maxTotal));
  const canMap = total > EPS && overPart.length === 0 && !overTotal;

  const submit = () => {
    if (!canMap) return;
    onConfirm(amounts.filter((a) => a.amount > EPS).map((a) => ({ pm: a.pm, amount: a.amount })));
  };

  // [FLUID-3 2026-10-12] Inert while it closes: nothing in it may be used during the 0.18 s exit.
  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }} $open={open} inert={closing}>
      <Box role="dialog" aria-modal="true" aria-label="Retention by PM" $open={open}>
        <Head>
          <h3>Retention by PM</h3>
          <span>{website} · payment {money(paymentAmount)}</span>
        </Head>
        <Body>
          <Note>Enter how much of this payment&apos;s retention goes to each PM. A PM can get at most their open part, and together the amounts can&apos;t be more than the payment.</Note>
          <Rows>
            {amounts.map((a, i) => (
              <Row key={a.pm} $bad={a.amount > a.open + EPS} data-pm-row={a.pm}>
                <span>
                  <div className="name">{a.pm}</div>
                  <div className="left">Part {money(a.part)} · up to {money(Math.max(0, a.open))}{a.others > EPS ? ` (has ${money(a.others)} on other payments)` : ''}</div>
                </span>
                <Amount
                  ref={i === 0 ? firstRef : undefined}
                  type="text"
                  inputMode="decimal"
                  aria-label={`Retention for ${a.pm}`}
                  placeholder="$0.00"
                  value={values[a.pm] === '' ? '' : `$${values[a.pm]}`}
                  onChange={(e) => setValues((v) => ({ ...v, [a.pm]: clean(e.target.value) }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
                />
              </Row>
            ))}
          </Rows>
          <Total $bad={overTotal} data-pm-total>
            <span>Total {money(total)}</span>
            <span>of {money(ceiling)} available on this payment</span>
          </Total>
          {overPart.map((a) => (
            <Err key={a.pm} role="alert">
              <b>Over {a.pm}&apos;s part.</b> {a.pm} can get up to {money(a.part)} on {website} this month
              {a.others > EPS ? <> and already has {money(a.others)} on other payments</> : null}
              {' '}— at most <b>{money(Math.max(0, a.open))}</b> here. Lower {a.pm}&apos;s amount.
            </Err>
          ))}
          {overTotal && (
            <Err role="alert">
              <b>More than the payment.</b> The amounts add up to {money(total)}, but this payment is {money(paymentAmount)}
              {otherCats > EPS ? <> and already maps {money(otherCats)} in other categories, so only {money(ceiling)} is free for retention</> : null}.
              {' '}Lower the amounts by <b>{money(round(total - ceiling))}</b>.
            </Err>
          )}
        </Body>
        <Foot>
          <Secondary type="button" onClick={onCancel}>Back</Secondary>
          <Primary type="button" disabled={!canMap} onClick={submit}>Map {total > EPS && canMap ? money(total) : 'retention'}</Primary>
        </Foot>
      </Box>
    </Overlay>
  );
};

export default RetentionPmPopup;
